
import { useMemo, useState, type DragEvent } from "react";
import { Button, Col, Empty, Input, Row } from "antd";
import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    DownloadOutlined,
    MenuOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import dayjs from "dayjs";

import { Client } from "../../core/api-client";
import { API_GET_SALES_LIST } from "../../shared/routes/private.routes";
import {
    ROUTE_MANAGE_SALES,
    ROUTE_SALES_WON,
    ROUTE_SALES_LOST,
} from "../../shared/routes/private.routes";
import { showNotification } from "../../shared/utils/utils";

import "../../modules/staff/export-staff.less";

type WorkflowStatus = "PIPELINE" | "WON" | "LOST";
type DragSource = "available" | "active";

interface ExportColumn {
    key: string;
    label: string;
    dataIndex: string;
    type?: "date" | "percentage";
}

interface SalesExportNavigationState {
    workflowStatus?: WorkflowStatus;
    filters?: Record<string, any>;
    sorter?: {
        sort_by?: string;
        sort_type?: string;
    };
    returnTo?: string;
}

const COMMON_COLUMNS: ExportColumn[] = [
    { key: "dealReference", label: "Deal Ref", dataIndex: "dealReference" },
    { key: "entityName", label: "Entity", dataIndex: "entityName" },
    {
        key: "receivedDate",
        label: "Sale Received Date",
        dataIndex: "receivedDate",
        type: "date",
    },
    { key: "clientName", label: "Client", dataIndex: "clientName" },
    {
        key: "clientCategory",
        label: "Client Category",
        dataIndex: "clientCategory",
    },
    {
        key: "clientLocation",
        label: "Location",
        dataIndex: "clientLocation",
    },
    {
        key: "projectTypeName",
        label: "Opportunity Type",
        dataIndex: "projectTypeName",
    },
    {
        key: "opportunity",
        label: "Opportunity Description",
        dataIndex: "opportunity",
    },
    {
        key: "leadOwnerName",
        label: "Lead Owner",
        dataIndex: "leadOwnerName",
    },
    {
        key: "deliveryOwnerName",
        label: "Delivery Owner",
        dataIndex: "deliveryOwnerName",
    },
    {
        key: "dealStageName",
        label: "Stage",
        dataIndex: "dealStageName",
    },
    {
        key: "dealSubStageName",
        label: "Sub-Stage",
        dataIndex: "dealSubStageName",
    },
    { key: "dealValue", label: "Deal Value", dataIndex: "dealValue" },
    {
        key: "dealCurrencyName",
        label: "Deal Currency",
        dataIndex: "dealCurrencyName",
    },
    {
        key: "dealValueTarget",
        label: "Deal Value (Reporting)",
        dataIndex: "dealValueTarget",
    },
    {
        key: "targetCurrencyName",
        label: "Reporting Currency",
        dataIndex: "targetCurrencyName",
    },
    {
        key: "grossMargin",
        label: "Gross Margin (%)",
        dataIndex: "grossMargin",
        type: "percentage",
    },
    {
        key: "quotationSowNumber",
        label: "Quotation/SOW Number",
        dataIndex: "quotationSowNumber",
    },
    {
        key: "newRenewal",
        label: "New/Renewal",
        dataIndex: "newRenewal",
    },
    { key: "remarks", label: "Remarks", dataIndex: "remarks" },
];

const PIPELINE_COLUMNS: ExportColumn[] = [
    {
        key: "estimatedClosureDate",
        label: "Estimated Closure Date",
        dataIndex: "estimatedClosureDate",
        type: "date",
    },
    {
        key: "closureQuarter",
        label: "Closure Quarter",
        dataIndex: "closureQuarter",
    },
    {
        key: "closureMonth",
        label: "Closure Month",
        dataIndex: "closureMonth",
    },
    {
        key: "probability",
        label: "Probability (%)",
        dataIndex: "probability",
        type: "percentage",
    },
    { key: "priority", label: "Priority", dataIndex: "priority" },
];

const WON_COLUMNS: ExportColumn[] = [
    {
        key: "closeDate",
        label: "Close Date",
        dataIndex: "closeDate",
        type: "date",
    },
    {
        key: "estimatedStartDate",
        label: "Estimated Start Date",
        dataIndex: "estimatedStartDate",
        type: "date",
    },
    {
        key: "estimatedEndDate",
        label: "Estimated End Date",
        dataIndex: "estimatedEndDate",
        type: "date",
    },
    {
        key: "addInYear",
        label: "Year Revenue",
        dataIndex: "addInYear",
    },
    {
        key: "sowPoNumber",
        label: "SOW/PO Number",
        dataIndex: "sowPoNumber",
    },
    {
        key: "durationMonths",
        label: "Duration (Months)",
        dataIndex: "durationMonths",
    },
];

const LOST_COLUMNS: ExportColumn[] = [
    {
        key: "lostDate",
        label: "Lost Date",
        dataIndex: "lostDate",
        type: "date",
    },
];

const SALES_EXPORT_COLUMNS: Record<WorkflowStatus, ExportColumn[]> = {
    PIPELINE: [...COMMON_COLUMNS, ...PIPELINE_COLUMNS],
    WON: [...COMMON_COLUMNS, ...WON_COLUMNS],
    LOST: [...COMMON_COLUMNS, ...LOST_COLUMNS],
};

const DEFAULT_SORTER = {
    sort_by: "updatedDate",
    sort_type: "DESC",
};

const ExportSalesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const navigationState =
        (location.state || {}) as SalesExportNavigationState;

    const workflowStatus: WorkflowStatus =
        navigationState.workflowStatus === "WON" ||
            navigationState.workflowStatus === "LOST"
            ? navigationState.workflowStatus
            : "PIPELINE";

    const filters = navigationState.filters || {};
    const sorter = navigationState.sorter || DEFAULT_SORTER;

    const viewName =
        workflowStatus === "PIPELINE"
            ? "Pipeline"
            : workflowStatus === "WON"
                ? "Won"
                : "Lost";

    const returnRoute =
        navigationState.returnTo ||
        (workflowStatus === "WON"
            ? ROUTE_SALES_WON
            : workflowStatus === "LOST"
                ? ROUTE_SALES_LOST
                : ROUTE_MANAGE_SALES);

    const exportColumns = useMemo(
        () => SALES_EXPORT_COLUMNS[workflowStatus],
        [workflowStatus]
    );

    const [activeExportColumns, setActiveExportColumns] = useState<string[]>(
        () => exportColumns.map((column) => column.key)
    );

    const [availableSearch, setAvailableSearch] = useState("");
    const [activeSearch, setActiveSearch] = useState("");
    const [exporting, setExporting] = useState(false);

    const [draggedColumnKey, setDraggedColumnKey] = useState<string | null>(
        null
    );
    const [dragSource, setDragSource] = useState<DragSource | null>(null);

    const availableExportColumns = exportColumns.filter(
        (column) => !activeExportColumns.includes(column.key)
    );

    const filteredAvailableExportColumns = availableExportColumns.filter(
        (column) =>
            column.label.toLowerCase().includes(availableSearch.toLowerCase())
    );

    const filteredActiveExportColumns = activeExportColumns
        .map((key) => exportColumns.find((column) => column.key === key))
        .filter(
            (column): column is ExportColumn =>
                Boolean(
                    column &&
                    column.label.toLowerCase().includes(activeSearch.toLowerCase())
                )
        );

    const addExportColumn = (key: string) => {
        setActiveExportColumns((previous) =>
            previous.includes(key) ? previous : [...previous, key]
        );
    };

    const removeExportColumn = (key: string) => {
        setActiveExportColumns((previous) =>
            previous.filter((columnKey) => columnKey !== key)
        );
    };

    const clearDragState = () => {
        setDraggedColumnKey(null);
        setDragSource(null);
    };

    const handleDragStart = (
        event: DragEvent<HTMLDivElement>,
        key: string,
        source: DragSource
    ) => {
        setDraggedColumnKey(key);
        setDragSource(source);
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", key);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    const handleDropOnActive = (
        event: DragEvent<HTMLDivElement>,
        targetKey?: string
    ) => {
        event.preventDefault();

        const sourceKey =
            draggedColumnKey || event.dataTransfer.getData("text/plain");

        if (!sourceKey) {
            clearDragState();
            return;
        }

        setActiveExportColumns((previous) => {
            const updated = [...previous];

            if (dragSource === "available") {
                if (updated.includes(sourceKey)) return previous;

                const targetIndex = targetKey
                    ? updated.indexOf(targetKey)
                    : updated.length;

                if (targetIndex === -1) {
                    return [...updated, sourceKey];
                }

                updated.splice(targetIndex, 0, sourceKey);
                return updated;
            }

            if (dragSource === "active") {
                const sourceIndex = updated.indexOf(sourceKey);
                const targetIndex = targetKey
                    ? updated.indexOf(targetKey)
                    : updated.length - 1;

                if (sourceIndex === -1 || targetIndex === -1) {
                    return previous;
                }

                const [movedColumn] = updated.splice(sourceIndex, 1);
                updated.splice(targetIndex, 0, movedColumn);

                return updated;
            }

            return previous;
        });

        clearDragState();
    };

    const handleDropOnAvailable = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        const sourceKey =
            draggedColumnKey || event.dataTransfer.getData("text/plain");

        if (sourceKey && dragSource === "active") {
            removeExportColumn(sourceKey);
        }

        clearDragState();
    };

    const selectAllExportColumns = () => {
        setActiveExportColumns(exportColumns.map((column) => column.key));
    };

    const removeAllExportColumns = () => {
        setActiveExportColumns([]);
    };

    const formatExportValue = (record: any, column: ExportColumn) => {
        const value = record?.[column.dataIndex];

        if (value === null || value === undefined || value === "") {
            return "";
        }

        if (column.type === "date") {
            return dayjs(value).isValid()
                ? dayjs(value).format("DD-MMM-YYYY")
                : "";
        }

        if (column.type === "percentage") {
            return value;
        }

        return value;
    };

    const exportRows = async () => {
        if (activeExportColumns.length === 0) {
            showNotification("Please select at least one column", "error");
            return;
        }

        setExporting(true);

        try {
            const exportFilters = Object.fromEntries(
                Object.entries(filters).filter(
                    ([, value]) =>
                        value !== undefined &&
                        value !== null &&
                        value !== "" &&
                        !(Array.isArray(value) && value.length === 0)
                )
            );

            // Do not pass page or limit. The existing Sales API returns
            // all matching records when pagination parameters are omitted.
            const response = await Client.post(
                API_GET_SALES_LIST,
                {
                    ...exportFilters,
                    workflowStatus,
                },
                {
                    params: {
                        sort_by: sorter.sort_by || "updatedDate",
                        sort_type: sorter.sort_type || "DESC",
                    },
                }
            );

            const rows = response?.data?.data ?? [];

            if (!Array.isArray(rows)) {
                throw new Error("Invalid Sales API response");
            }

            if (rows.length === 0) {
                showNotification("No Sales records found for export", "error");
                return;
            }

            const selectedColumns = activeExportColumns
                .map((key) => exportColumns.find((column) => column.key === key))
                .filter((column): column is ExportColumn => Boolean(column));

            const csvRows = rows.map((record: any) =>
                Object.fromEntries(
                    selectedColumns.map((column) => [
                        column.label,
                        formatExportValue(record, column),
                    ])
                )
            );

            const csv = Papa.unparse(csvRows, {
                escapeFormulae: true,
                columns: selectedColumns.map((column) => column.label),
            });

            const blob = new Blob(["\uFEFF", csv], {
                type: "text/csv;charset=utf-8;",
            });

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = `${workflowStatus.toLowerCase()}-sales.csv`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);

            showNotification(
                `Successfully exported ${rows.length} ${viewName} Sales records`,
                "success"
            );
        } catch (error: any) {
            console.error("Sales export failed:", error);

            showNotification(
                error?.response?.data?.message ||
                `Unable to export ${viewName} Sales records`,
                "error"
            );
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="app-page staff-export-page">
            <div className="it-export-page-header">
                <div className="it-export-page-heading">
                    <div className="it-export-page-icon">
                        <DownloadOutlined />
                    </div>

                    <div>
                        <h1>Export {viewName} Sales</h1>
                        <p>
                            Select, organize and customize the columns for your export.
                        </p>
                    </div>
                </div>

                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate(returnRoute, { replace: true })}
                >
                    Back to Manage {viewName}
                </Button>
            </div>

            <div className="it-export-screen">
                <Row gutter={[20, 20]} className="it-export-panels">
                    <Col xs={24} md={12}>
                        <div className="it-export-panel">
                            <div className="it-export-panel-header">
                                <div>
                                    <h3>Available Columns</h3>
                                    <span>{availableExportColumns.length} columns</span>
                                </div>

                                <Button
                                    type="link"
                                    onClick={selectAllExportColumns}
                                    disabled={availableExportColumns.length === 0}
                                >
                                    Select All
                                </Button>
                            </div>

                            <div className="it-export-panel-search">
                                <Input
                                    allowClear
                                    placeholder="Search available columns..."
                                    value={availableSearch}
                                    onChange={(event) => setAvailableSearch(event.target.value)}
                                />
                            </div>

                            <div
                                className="it-export-column-list"
                                onDragOver={handleDragOver}
                                onDrop={handleDropOnAvailable}
                            >
                                {filteredAvailableExportColumns.length > 0 ? (
                                    filteredAvailableExportColumns.map((column) => (
                                        <div
                                            className={`it-export-column-item ${draggedColumnKey === column.key
                                                    ? "it-export-column-dragging"
                                                    : ""
                                                }`}
                                            key={column.key}
                                            draggable
                                            onDragStart={(event) =>
                                                handleDragStart(event, column.key, "available")
                                            }
                                            onDragEnd={clearDragState}
                                        >
                                            <MenuOutlined className="it-export-drag-icon" />
                                            <span>{column.label}</span>

                                            <Button
                                                type="text"
                                                className="it-export-action-button"
                                                icon={<PlusOutlined />}
                                                title={`Add ${column.label}`}
                                                onClick={() => addExportColumn(column.key)}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="it-export-empty">
                                        {availableExportColumns.length === 0 ? (
                                            <>
                                                <CheckCircleOutlined className="it-export-empty-icon" />
                                                <h4>All columns selected</h4>
                                                <p>
                                                    All available columns are already included in your
                                                    export.
                                                </p>
                                            </>
                                        ) : (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description="No matching columns"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>

                    <Col xs={24} md={12}>
                        <div className="it-export-panel">
                            <div className="it-export-panel-header">
                                <div>
                                    <h3>Active Columns</h3>
                                    <span>
                                        {activeExportColumns.length} columns selected
                                    </span>
                                </div>

                                <Button
                                    type="link"
                                    onClick={removeAllExportColumns}
                                    disabled={activeExportColumns.length === 0}
                                >
                                    Remove All
                                </Button>
                            </div>

                            <div className="it-export-panel-search">
                                <Input
                                    allowClear
                                    placeholder="Search active columns..."
                                    value={activeSearch}
                                    onChange={(event) => setActiveSearch(event.target.value)}
                                />
                            </div>

                            <div
                                className="it-export-column-list"
                                onDragOver={handleDragOver}
                                onDrop={(event) => handleDropOnActive(event)}
                            >
                                {filteredActiveExportColumns.length > 0 ? (
                                    filteredActiveExportColumns.map((column) => (
                                        <div
                                            className={`it-export-column-item ${draggedColumnKey === column.key
                                                    ? "it-export-column-dragging"
                                                    : ""
                                                }`}
                                            key={column.key}
                                            draggable
                                            onDragStart={(event) =>
                                                handleDragStart(event, column.key, "active")
                                            }
                                            onDragOver={handleDragOver}
                                            onDrop={(event) =>
                                                handleDropOnActive(event, column.key)
                                            }
                                            onDragEnd={clearDragState}
                                        >
                                            <MenuOutlined className="it-export-drag-icon" />
                                            <span>{column.label}</span>

                                            <Button
                                                type="text"
                                                className="it-export-action-button"
                                                icon={<CloseOutlined />}
                                                title={`Remove ${column.label}`}
                                                onClick={() => removeExportColumn(column.key)}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="it-export-empty">
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description="Drag columns here or add them from Available Columns"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                <div className="it-export-footer">
                    <div className="it-export-selection-summary">
                        <span className="it-export-selection-count">
                            {activeExportColumns.length}
                        </span>

                        <span>
                            {activeExportColumns.length === 1
                                ? "column selected"
                                : "columns selected"}
                        </span>
                    </div>

                    <div className="it-export-footer-actions">
                        <Button
                            onClick={() => navigate(returnRoute, { replace: true })}
                            disabled={exporting}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            disabled={activeExportColumns.length === 0}
                            loading={exporting}
                            onClick={exportRows}
                        >
                            Export CSV
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportSalesPage;
